import { useState, useRef, useCallback, useEffect } from 'react'
import { Search, Check, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/utils/cn'
import { useUserSearch } from '@/hooks/useUserSearch'
import { usePlanShares } from '@/hooks/usePlanShares'
import { useTransferOwnership } from '@/hooks/useTransferOwnership'
import { toast } from '@/stores/useToastStore'
import type { UserRead } from '@/api/shares'

interface TransferOwnershipModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onTransfer: () => void
  planId: string
}

// Generate initials from display_name or email
function getInitials(displayName: string | null, email: string | null): string {
  if (displayName) {
    const parts = displayName.trim().split(/\s+/)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return displayName.slice(0, 2).toUpperCase()
  }
  if (email) {
    return email.slice(0, 2).toUpperCase()
  }
  return '??'
}

// Avatar color combinations
const avatarColors = [
  { bg: 'bg-[#dbeafe]', text: 'text-[#155dfc]' },
  { bg: 'bg-[#dcfce7]', text: 'text-[#00a63e]' },
  { bg: 'bg-[#fef3c7]', text: 'text-[#d97706]' },
  { bg: 'bg-[#fce7f3]', text: 'text-[#db2777]' },
  { bg: 'bg-[#e0e7ff]', text: 'text-[#4f46e5]' },
]

function getAvatarColor(id: string | number) {
  const index =
    typeof id === 'string'
      ? id.charCodeAt(0) % avatarColors.length
      : id % avatarColors.length
  return avatarColors[index]
}

export function TransferOwnershipModal({
  open,
  onOpenChange,
  onTransfer,
  planId,
}: TransferOwnershipModalProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<UserRead | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Hooks
  const {
    data: searchData,
    isLoading: isSearching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useUserSearch({ query: searchQuery })
  const { data: sharesData } = usePlanShares(planId)
  const transferMutation = useTransferOwnership(planId)

  // Current owner ID from shares data
  const currentOwnerId = sharesData?.owner?.id

  // Flatten pages to get all users
  const users = searchData?.pages.flatMap(page => page.users) ?? []

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!listRef.current || !hasNextPage || isFetchingNextPage) return

    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setSelectedUser(null)
    setShowDropdown(true)
  }

  const handleInputFocus = () => {
    if (searchQuery.length >= 2) {
      setShowDropdown(true)
    }
  }

  const handleSelectUser = (user: UserRead) => {
    setSelectedUser(user)
    setSearchQuery(user.email || user.display_name || '')
    setShowDropdown(false)
  }

  const handleTransfer = () => {
    if (!selectedUser) return

    transferMutation.mutate(
      { newOwnerId: String(selectedUser.id) },
      {
        onSuccess: () => {
          onTransfer()
          setSelectedUser(null)
          setSearchQuery('')
        },
        onError: (error: Error) => {
          toast.error(`Failed to transfer ownership: ${error.message}`)
        },
      }
    )
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedUser(null)
      setSearchQuery('')
      setShowDropdown(false)
    }
    onOpenChange(isOpen)
  }

  // Check if selected user is current owner
  const isSelectedUserCurrentOwner =
    selectedUser && currentOwnerId && String(selectedUser.id) === currentOwnerId

  // Show dropdown only when query >= 2 chars
  const shouldShowDropdown = showDropdown && searchQuery.length >= 2

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="z-[60] sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Transfer Ownership</DialogTitle>
          <DialogDescription>
            Select a new owner for this assortment plan.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Search input */}
          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={inputRef}
                placeholder="Search users..."
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                className="pl-9"
              />
            </div>

            {/* Search Dropdown */}
            {shouldShowDropdown && (
              <div
                ref={listRef}
                onScroll={handleScroll}
                className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg max-h-[300px] overflow-y-auto z-50"
              >
                {isSearching && !isFetchingNextPage ? (
                  <div className="px-4 py-6 text-center text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                    <span className="text-sm">Searching...</span>
                  </div>
                ) : users.length > 0 ? (
                  <div className="py-1">
                    {users.map(user => {
                      const isCurrentOwner = Boolean(
                        currentOwnerId && String(user.id) === currentOwnerId
                      )
                      const isSelected = selectedUser?.id === user.id
                      const colors = getAvatarColor(user.id)

                      return (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() =>
                            !isCurrentOwner && handleSelectUser(user)
                          }
                          className={cn(
                            'flex w-full cursor-pointer items-center justify-between rounded-lg p-3 text-left transition-colors',
                            isCurrentOwner
                              ? 'cursor-default bg-muted/50'
                              : isSelected
                                ? 'bg-[#fef2f2]'
                                : 'hover:bg-muted'
                          )}
                          disabled={isCurrentOwner}
                        >
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarFallback
                                className={cn(
                                  colors.bg,
                                  colors.text,
                                  'text-xs font-medium'
                                )}
                              >
                                {getInitials(user.display_name, user.email)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium">
                                {user.display_name || user.email}
                              </p>
                              {user.display_name && user.email && (
                                <p className="text-xs text-muted-foreground">
                                  {user.email}
                                </p>
                              )}
                            </div>
                          </div>
                          {isCurrentOwner && (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Check className="h-4 w-4" />
                              <span>Current owner</span>
                            </div>
                          )}
                          {isSelected && !isCurrentOwner && (
                            <Check className="h-4 w-4 text-[#CF1F2E]" />
                          )}
                        </button>
                      )
                    })}
                    {isFetchingNextPage && (
                      <div className="px-4 py-3 text-center">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="px-4 py-6 text-center text-muted-foreground">
                    <span className="text-sm">
                      No users found for "{searchQuery}"
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={handleTransfer}
            disabled={
              !selectedUser ||
              isSelectedUserCurrentOwner ||
              transferMutation.isPending
            }
            className="bg-[#CF1F2E] hover:bg-[#b81a27] cursor-pointer"
          >
            {transferMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Transfer Ownership'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
